/* eslint-disable lingui/no-unlocalized-strings */
import * as Sentry from '@sentry/react';
import { useStytchB2BClient } from '@stytch/react/b2b';
import { useRef } from 'react';
import useWebSocketModule, { ReadyState } from 'react-use-websocket';

const useWebSocket = (useWebSocketModule as unknown as { default: typeof useWebSocketModule })
  .default;

import { FEATURE_FLAG } from '@/constants/featureFlags';
import { useFeatureFlag } from '@/hooks/useFeatureFlag';
import { useMe } from '@/providers/User';

import {
  type ClientMessagePayload,
  type ClientMessageType,
  type WebSocketAIMessage,
  type WebSocketError,
  type WebSocketResponse,
  isUnknownWebSocketMessage,
  isWebSocketError,
  parseWebSocketResponse,
} from './message';

type AuthWebSocketParams = {
  onMessageReceived?: (message: WebSocketAIMessage) => void;
  onErrorReceived?: (message: WebSocketError) => void;
};

const useAuthWebSocket = ({ onMessageReceived, onErrorReceived }: AuthWebSocketParams) => {
  const currentUser = useMe();
  const isMcpServerEnabled = useFeatureFlag(FEATURE_FLAG.MCP_SERVER);
  const { session } = useStytchB2BClient();
  const stopSpanRef = useRef<() => void>(undefined);

  const jwtToken = session.getTokens()?.session_jwt;

  const websocketUrl = isMcpServerEnabled
    ? import.meta.env.VITE_MCP_SERVER_WS_URL
    : import.meta.env.VITE_WEBSOCKET_URL;

  const socket = useWebSocket<WebSocketResponse>(websocketUrl, {
    protocols: jwtToken,
    onOpen: () => {
      Sentry.addBreadcrumb({
        type: 'default',
        category: 'AI Websocket Opened',
        message: 'Socket opened',
        level: 'info',
      });
    },
    onClose: (event) => {
      Sentry.addBreadcrumb({
        type: 'default',
        category: 'AI Websocket Closed',
        message: 'Socket closed',
        data: {
          code: event.code,
          reason: event.reason,
        },
        level: 'error',
      });
    },
    onError: (event) => {
      console.error('Error connecting to websocket:', event);
    },
    retryOnError: true,
    reconnectAttempts: Infinity,
    reconnectInterval: (attempt) => Math.min(1000 * Math.pow(2, attempt), 30000),
    shouldReconnect: () => true,
    onMessage: (event: MessageEvent) => {
      let parsedEventData: unknown;
      try {
        parsedEventData = JSON.parse(event.data);
      } catch (error) {
        console.error('Failed to parse WebSocket message:', error, event.data);
        Sentry.captureException(error, {
          extra: { message: 'Failed to parse WebSocket message', rawData: event.data },
        });
        return;
      }

      const eventData = parseWebSocketResponse(parsedEventData);
      if (!eventData) {
        console.error('Invalid websocket message:', parsedEventData);
        Sentry.captureException(new Error('Invalid websocket message'), {
          extra: { rawData: parsedEventData },
        });
        return;
      }

      // Log and ignore any traffic that is not a valid websocket message
      if (!isWebSocketError(eventData) && isUnknownWebSocketMessage(eventData)) {
        console.error('Unknown websocket message:', eventData);
        Sentry.captureException(new Error('Unknown websocket message'), {
          tags: eventData,
        });
        return;
      }

      if (eventData.type === 'complete' || eventData.type === 'error') {
        Sentry.addBreadcrumb({
          type: 'default',
          category: 'Chat Response',
          message: eventData.type === 'error' ? 'AI error received' : 'AI message received',
          data: eventData,
          level: eventData.type === 'error' ? 'error' : 'info',
        });
      }

      // These are known errors to MCP Server, so we can capture, log, and display them
      if (eventData.type === 'error') {
        Sentry.captureException(new Error(eventData.payload.code), {
          tags: {
            'mcp:code': eventData.payload.code,
            'mcp:message': eventData.payload.content,
            'mcp:sessionId': eventData.payload.sessionId,
          },
        });
        onErrorReceived?.(eventData);
      } else {
        onMessageReceived?.(eventData);
      }

      if (eventData.type === 'complete' || eventData.type === 'error') {
        // end the span if the full message has been fully streamed or an error occurred
        stopSpanRef.current?.();
      }
    },
  });

  const handleSendMessage = (payload: ClientMessagePayload) => {
    Sentry.startSpanManual({ name: 'AI websocket message' }, (span, finishSpan) => {
      Sentry.addBreadcrumb({
        type: 'user',
        category: 'AI Chat Request',
        message: payload.content,
        data: payload,
        level: 'info',
      });

      const message: ClientMessageType = {
        type: 'message',
        token: session.getTokens()?.session_jwt,
        instanceId: currentUser?.currentOrg.instanceId,
        payload,
        sentryMetadata: {
          sentryTrace: Sentry.spanToTraceHeader(span),
          baggage: Sentry.spanToBaggageHeader(span),
        },
      };

      socket?.sendJsonMessage(message);

      // Conclude the span when we receive the response
      stopSpanRef.current = finishSpan;
    });
  };

  const connectionStatus = {
    [ReadyState.CONNECTING]: 'Connecting',
    [ReadyState.OPEN]: 'Open',
    [ReadyState.CLOSING]: 'Closing',
    [ReadyState.CLOSED]: 'Closed',
    [ReadyState.UNINSTANTIATED]: 'Uninstantiated',
  }[socket?.readyState ?? ReadyState.UNINSTANTIATED];

  return {
    onSendMessage: handleSendMessage,
    connectionStatus,
  };
};

export default useAuthWebSocket;
